import { Injectable } from '@angular/core';
import { BdGraph, BdGraphNode, BdGraphStateMap, GraphNodeState } from '../../../../lib/graph/graph.model';
import * as deepEqual from 'fast-deep-equal';
import { ChapterModel } from '../../../story/models/chapter.model';
import { TimelineModel } from '../../../story/models/timeline.model';
import { uuid } from '../../../shared/uuid';
import { HotspotModel } from '../../../story/models/labels/hotspot.model';
import { BackgroundModel } from '../../../engine/models/background.model';
import { classToClass } from 'class-transformer';
import { omit, isEmpty } from 'lodash';
import { LabelsService } from '../../../story/labels.service';

import { calculateLeafStateMap } from 'src/lib/graph/leaf-state';
import { ErrandType } from '../../../story/models/errands/errand-type.enum';
import { calculateMiniminumSynchronization } from './minimum-synchronization';
import { renamespaceValue, renamespaceKeys } from '../renamespace-utils';
import { GraphUtils } from 'src/lib/graph/graph-utils';


declare const Human: any;

// next human engine version
function getRecipes(): string[] {
  if (Human.scene.colorRecipes) {
    return Human.scene.colorRecipes.getRecipes();
  } else {
    return [];
  }
}



// rules for propagating state up our BdGraph
class GraphNodeSync {
  shown = 'majorityWins';
  selected = 'majorityWins';
  pickable = 'majorityWins';

  constructor(recipes: string[]) {
    recipes.forEach((recipeName) => {
      this[recipeName] = 'majorityWins';
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class SerializeChapterService {

  constructor(
    private labelService: LabelsService
  ) { }


  // Query the synchronization and produce the minimum graph synchronization object
  private getMinimumGraphSync(graph: BdGraph<BdGraphNode>, sync: BdGraphStateMap, namespaceMap: { [key: string]: string }): BdGraphStateMap {
    // calculate leaf state based on synchronization


    // TODO: when are recipes defined?
    const recipes = getRecipes();


    const properties = Object.keys(new GraphNodeState(recipes)); // ["shown", "selected", "pickable"]


    // calculate the state of all leaves given the sync object.
    // leaf state is needed to then propagate back up the tree to build the min sync.
    const leafState = calculateLeafStateMap(graph, sync, properties);

    // minimize sync with inheritable properties: calculate graph state via upward propagation of leaf state
    const syncRules = JSON.parse(JSON.stringify(new GraphNodeSync(recipes)));

    const minSync = calculateMiniminumSynchronization(graph, syncRules, leafState);

    // copy over non-inheritable properties
    Object.entries(sync).forEach(([objectId, objectState]) => {
      const diffSync = omit(objectState, properties);
      if (!isEmpty(diffSync)) {
        minSync[objectId] = { ...minSync[objectId] || {}, ...diffSync };
      }
    });


    const renamedMinSync = new BdGraphStateMap();
    Object.entries(minSync).forEach(([id, syncState]) => {
      renamedMinSync[this.switchNamespace(id, namespaceMap)] = syncState;
    }, this);

    return renamedMinSync;
  }

  // Convert synchronization object to chapter properties
  private convertGraphSync(sync: BdGraphStateMap): any {
    const showObjects = {};
    const selectObjects = {};
    const pickThroughObjects = {};
    const colorRecipeObjects = {};
    const synchronization = {};
    const recipes = getRecipes();

    Object.entries(sync).forEach(([id, stateMap]) => {
      Object.entries(stateMap).forEach(([property, value]) => {
        if (property === 'shown') {
          showObjects[id] = value;
        } else if (property === 'selected') {
          selectObjects[id] = value;
        } else if (property === 'pickable') {
          pickThroughObjects[id] = !value;
        } else if (recipes.find(recipeName => recipeName === property)) {
          colorRecipeObjects[property] = { ...colorRecipeObjects[property], ...{ [id]: value } };
        } else {
          // keep in the synchronization object directly
          synchronization[id] = { ...synchronization[id] || {}, ...{ [property]: value } };
        }
      }, this);

    }, this);

    return {
      showObjects: showObjects,
      selectObjects: selectObjects,
      pickThroughObjects: pickThroughObjects,
      colorRecipeObjects: colorRecipeObjects,
      synchronization: synchronization
    };
  }

  private switchNamespace(id: string, namespaceMap: { [key: string]: string }): string {
    return namespaceMap[id] || id;
  }


  // Serialize timeline maps
  private serializeTimelineMaps(chapter: ChapterModel, namespaceMap: { [key: string]: string }): TimelineModel[] {

    const timelineMaps: TimelineModel[] = chapter.timelineMap;
    timelineMaps.forEach((tmap) => {
      tmap.data.timeline = this.switchNamespace(tmap.data.timeline, namespaceMap);
    });
    return timelineMaps;
  }

  // serialize engine properties - Only non-default properties 
  private serializeProperties(huChapter: any): any {
    const properties: any = {};

    const defaults = Human.properties.getDefaults();
    Object.entries(huChapter.properties).forEach(([property, value]) => {
      if (!deepEqual(value, defaults[property])) {
        properties[property] = value;
      }
    }, this);

    // legacy properties to eventually be discarded
    const annotationLayout = Human.properties.properties['annotations.layout.type'];
    if (annotationLayout !== 'draggable') {
      properties['annotations.layout.type'] = annotationLayout;
    }

    return properties;
  }

  // serialize labels and hotspots
  private serializeHotspots(chapter: ChapterModel, reverseNamespaces): { customData: any, hotspots: HotspotModel[] } {
    let hotspots: HotspotModel[] = [...chapter.hotspots];
    let customData = {};
    chapter.labels.forEach((label) => {
      const labelData = this.labelService.serializeLabel(label, hotspots.length, reverseNamespaces);
      hotspots = [...hotspots, ...labelData.hotspots];
      customData = { ...customData, ...labelData.data };
    });

    return {
      customData: customData,
      hotspots: hotspots
    };
  }

  // serialize background
  private serializeBackground(background: BackgroundModel): BackgroundModel | {} {
    if (!!background) {
      return classToClass(background);
    } else {
      return {};
    }
  }

  // serialize ui
  private serializeUI(huChapter: any, ui: any) {
    if (isEmpty(ui)) {
      return { play: { count: 1 } };
    } else {
      return ui;
    }
  }

  // serialize chapter model into chapter data
  private serializeChapter(
    graph: BdGraph<BdGraphNode>,
    chapter: ChapterModel,
    inplace: boolean = false,
    namespaceMap: { [key: string]: string },
    replaceObjectId: boolean = false
  ): { chapter: any, sourceId: string } {


    const previousLabelNamespaces = chapter.labels.map(label => label.objectId);
  
    if (replaceObjectId) {
      // Iterate through labels and replace object IDS with new namespaces
      chapter.labels.forEach(label => {
        label.objectId = renamespaceValue(label.objectId, namespaceMap);

        label.states.forEach(state => {
          state.errands.forEach(errand => {
            switch (errand.type) {
              case ErrandType.flyToChapter:
              case ErrandType.flyToObject:
                errand.value = renamespaceValue(errand.value, namespaceMap);
            }
          });
        });
      });
    }

    // for all labels, make a reverse namespace map
    const reverseNamespaces = replaceObjectId
      ? previousLabelNamespaces.reduce((acc, labelObjectId) => {
        const newNamespace = renamespaceValue(labelObjectId, namespaceMap);
        const oldNamespace = labelObjectId;
        return {
          ...acc,
          [newNamespace]: oldNamespace
        };
      }, {}) 
      : undefined;


    // Hotspots are generated from labels, so we perform objectID conversion on labels before
    // generating the hotspots. 
    const hotspotData = this.serializeHotspots(chapter, reverseNamespaces);
    const customData = { ...chapter.customData, ...hotspotData.customData };

    const chapterData = {
      id: inplace ? chapter.id : uuid.idWithPrefix('chapter'),
      displayName: chapter.info.displayName,
      description: chapter.info.description,
      [chapter.camera.action]: chapter.camera.location,
      background: this.serializeBackground(chapter.background),
      hotspots: hotspotData.hotspots,
      clippingPlanes: chapter.clippingPlanes,
      ui: this.serializeUI(chapter, chapter.ui),
      graph: chapter.graph || {},
      timelineMap: this.serializeTimelineMaps(chapter, namespaceMap),
      customData: customData,
      viewModes: chapter.viewModes,
      lights: chapter.lights,
      recipes: chapter.recipes || {},
      transparencyMode: chapter.transparencyMode || '',
      fallbackTransparency: chapter.fallbackTransparency || '',
      properties: this.serializeProperties(chapter),
      effects: chapter.effects || {},
      options: chapter.options,
      environment: chapter.environment || {}
    };

    // get data for restoring graph state

    const minSync = this.getMinimumGraphSync(graph, chapter.synchronization, namespaceMap);
    Object.entries(this.convertGraphSync(minSync))
      .forEach(([key, val]) => {
        chapterData[key] = val;
      });

    if (replaceObjectId) {
      // Iterate through chapter properties and change the keys to new namespaces
      // e.g.: "showObjects": { "test_alzheimers_disease_brain_cross_section-root": true },
      ['showObjects', 'selectObjects', 'pickThroughObjects', 'colorRecipeObjects', 'synchronization'].forEach(value => {
        if (chapterData.hasOwnProperty(value)) {
          chapterData[value] = renamespaceKeys(chapterData[value], namespaceMap);
        }
      });
    }

    return {
      chapter: chapterData,
      sourceId: chapter.id
    };
  }

  // serialize a single chapter from state
  serializeSingleChapter(chapter: ChapterModel, inplace: boolean = false, namespaceMap: { [key: string]: string } = {}): { chapter: any, sourceId: string } {
    const graphNodeData = new GraphNodeState(getRecipes());
    const graph = GraphUtils.createGraphFromSrc(Object.values(Human.scene.rootObjects),
      'objectId', 'objects', graphNodeData);

    return this.serializeChapter(graph, chapter, inplace, namespaceMap);
  }

  // serialize all chapters in state
  serialize(
    chapters: ChapterModel[],
    inplace: boolean = false,
    namespaceMap: { [key: string]: string },
    replaceObjectId: boolean
  ): { chapters: any[], sourceIdMap: any } {
    // prepare graph for graph state calculations
    const graphNodeData = new GraphNodeState(getRecipes());

    // #NOTE: [EM] This could be sped at by stashing the scene-graph on story load
    const graph = GraphUtils.createGraphFromSrc(Object.values(Human.scene.rootObjects),
      'objectId', 'objects', graphNodeData);

    // serialize each chapter
    const chaptersData = [];
    const chapterIdMaps = {};
    chapters.forEach((chapter) => {
      const serialized = this.serializeChapter(graph, chapter, inplace, namespaceMap, replaceObjectId);
      chapterIdMaps[serialized.chapter.id] = serialized.sourceId;
      chaptersData.push(serialized.chapter);
    });

    return {
      chapters: chaptersData,
      sourceIdMap: chapterIdMaps // TODO: what is this???
    };

  }
}

